import React from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn, useIsLoggingIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import type { Location } from '@order/graphql';
import { useDeliveryRestaurantQuery } from '@order/graphql';
import { useCart } from '@order/hooks';
import { useOrders } from '@order/Order';
import { useTrackEffect } from '@order/Telemetry';

import { useGetOrderStatusQuery } from './components/WelcomePane/OrderStatus.query.generated';
import { useOrderPendingFeedbackId } from './hooks';

export const useHomeData = () => {
  // ─── Cart & Customer ─────────────────────────────────────────────
  const { isCartEmpty, cartOrderType } = useCart();
  const { customer, isFetchingCustomer } = useCustomer();
  const customerAddress = customer?.addresses && customer.addresses[0];

  // ─── Auth ────────────────────────────────────────────────────────
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const isCustomerReady =
    !isLoggingIn &&
    !isFetchingCustomer &&
    (!isLoggedIn || Boolean(customer?.id));

  // ─── Order History ───────────────────────────────────────────────
  const { orders, isLoadingOrders, hasErrorOrders, fetchOrders } = useOrders();

  // ─── In Store Order Last Pending Feedback ────────────────────────────────
  const { orderPendingFeedbackId, reFetchFeedback } =
    useOrderPendingFeedbackId();

  // ─── Order Status ───────────────────────────────────────────────
  const [statusResponse, reFetchStatus] = useGetOrderStatusQuery({
    requestPolicy: 'network-only',
    pause: true,
  });
  const hasErrorStatus = Boolean(statusResponse.error);
  const isLoadingStatus = statusResponse.fetching || statusResponse.stale;
  const status = !isLoadingStatus ? statusResponse.data : undefined;

  // ─── Delivery Restaurant ───────────────────────────────────
  const customerDeliveryAddressId = customerAddress?.id ?? '';
  const [restaurantResponse] = useDeliveryRestaurantQuery({
    variables: { input: { addressId: customerDeliveryAddressId } },
    pause: !customerDeliveryAddressId,
  });

  const isLoadingDeliveryRestaurant =
    restaurantResponse.fetching || restaurantResponse.stale;
  const locationByAddressResponse = restaurantResponse.data
    ?.locationByAddress as Location;
  const deliveryRestaurant = locationByAddressResponse?.restaurant;
  const deliveryVendor = locationByAddressResponse?.vendor;

  // ─── Refetch on Focus if Logged In ──────────────────────────────────
  useRefetchOnFocusIfLoggedIn(fetchOrders, isLoggedIn);
  useRefetchOnFocusIfLoggedIn(reFetchStatus, isLoggedIn);
  useRefetchOnFocusIfLoggedIn(reFetchFeedback, isLoggedIn);

  // ─── Telemetry ──────────────────────────────────────────────────────

  useTrackEffect('home.view');

  return {
    orders,
    status,
    orderPendingFeedbackId,
    deliveryRestaurant,
    deliveryVendor,
    cartOrderType,
    isCartEmpty,
    isLoggedIn,
    isCustomerReady,
    isLoading: !isCustomerReady || isLoggingIn || isLoadingDeliveryRestaurant,
    isLoadingOrders: isLoadingOrders || isLoadingStatus,
    hasErrors: hasErrorOrders || hasErrorStatus,
  };
};

const useRefetchOnFocusIfLoggedIn = (fn: () => void, isLoggedIn: boolean) => {
  useFocusEffect(
    React.useCallback(() => {
      if (isLoggedIn) fn();
    }, [isLoggedIn, fn]),
  );
};
